/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AttachmentEntityType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface AttachmentProps {
  id: string;
  attachmentEntityType: AttachmentEntityType;
  entityId: string | null;
  fileName: string;
  fileSizeInBytes: number | null;
  contentType: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class AttachmentRecord extends Record<AttachmentProps>({
  id: null as any,
  attachmentEntityType: null as any,
  entityId: null as any,
  fileName: null as any,
  fileSizeInBytes: null as any,
  contentType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    attachmentEntityType: AttachmentEntityType;
    entityId: string | null;
    fileName: string;
    fileSizeInBytes: number | null;
    contentType: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): AttachmentRecord {
    if (json == null) {
      throw new Error('AttachmentRecord.fromJS: parameter cannot be null');
    }
    return new AttachmentRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'AttachmentJson.id',
      ),
      attachmentEntityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'attachmentEntityType', AttachmentEntityType.Unspecified),
        'AttachmentJson.attachmentEntityType',
      ),
      entityId: json.entityId,
      fileName: getFromOrThrow(json, (j) => j.fileName, 'AttachmentJson.fileName'),
      fileSizeInBytes: json.fileSizeInBytes,
      contentType: json.contentType,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    attachmentEntityType: AttachmentEntityType;
    entityId: string | null;
    fileName: string;
    fileSizeInBytes: number | null;
    contentType: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [AttachmentRecord, Error[]] {
    if (json == null) {
      throw new Error('AttachmentRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new AttachmentRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'AttachmentJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      attachmentEntityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'attachmentEntityType', AttachmentEntityType.Unspecified),
            'AttachmentJson.attachmentEntityType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () => json.entityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fileName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.fileName, 'AttachmentJson.fileName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fileSizeInBytes: tryOrDefault(
        () => json.fileSizeInBytes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      contentType: tryOrDefault(
        () => json.contentType,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: AttachmentProps) {
    super(props);
    if (props == null) {
      throw new Error('AttachmentRecord constructor parameter cannot be null');
    }
  }
}

export abstract class AttachmentViewModelBase implements AttachmentProps {
  protected readonly _props: AttachmentProps;
  private _json?: AttachmentProps;

  get id(): string {
    return this._props.id;
  }
  get attachmentEntityType(): AttachmentEntityType {
    return this._props.attachmentEntityType;
  }
  get entityId(): string | null {
    return this._props.entityId;
  }
  get fileName(): string {
    return this._props.fileName;
  }
  get fileSizeInBytes(): number | null {
    return this._props.fileSizeInBytes;
  }
  get contentType(): string | null {
    return this._props.contentType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCard() {
    return this._props.attachmentEntityType === AttachmentEntityType.Card;
  }
  public get isReleaseNote() {
    return this._props.attachmentEntityType === AttachmentEntityType.ReleaseNote;
  }
  public get isSupportRequest() {
    return this._props.attachmentEntityType === AttachmentEntityType.SupportRequest;
  }
  public get isUnspecified() {
    return this._props.attachmentEntityType === AttachmentEntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.attachmentEntityType === AttachmentEntityType.Vehicle;
  }

  public isAnyAttachmentEntityType(...attachmentEntityType: AttachmentEntityType[]) {
    return attachmentEntityType.includes(this._props.attachmentEntityType);
  }

  constructor(props: AttachmentProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        attachmentEntityType: this._props.attachmentEntityType,
        entityId: this._props.entityId,
        fileName: this._props.fileName,
        fileSizeInBytes: this._props.fileSizeInBytes,
        contentType: this._props.contentType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllAttachmentEntities = (state: AppState) =>
  state.entityStores.attachment.entities;

export const selectAttachmentById = (id: string) => (state: AppState) =>
  state.entityStores.attachment.entities.get(id);

export const selectAttachmentMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.attachment.meta.get(id);

export const selectAttachmentAndMetadataById = (id: string) =>
  createSelector(
    selectAttachmentById(id),
    selectAttachmentMetadataById(id),
    (attachment, meta) => ({ attachment, meta }),
  );

export function selectAttachmentEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllAttachmentEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, AttachmentRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectAttachmentEntitiesByAttachmentEntityType(
  attachmentEntityType: AttachmentEntityType,
) {
  return createSelector(selectAllAttachmentEntities, (entities) =>
    entities.filter((e) => e.attachmentEntityType === attachmentEntityType).valueSeq(),
  );
}

export function selectAttachmentEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllAttachmentEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectAttachmentEntitiesByFileName(fileName: string) {
  return createSelector(selectAllAttachmentEntities, (entities) =>
    entities.filter((e) => e.fileName === fileName).valueSeq(),
  );
}

export function selectAttachmentEntitiesByFileSizeInBytes(fileSizeInBytes: number) {
  return createSelector(selectAllAttachmentEntities, (entities) =>
    entities.filter((e) => e.fileSizeInBytes === fileSizeInBytes).valueSeq(),
  );
}

export function selectAttachmentEntitiesByContentType(contentType: string) {
  return createSelector(selectAllAttachmentEntities, (entities) =>
    entities.filter((e) => e.contentType === contentType).valueSeq(),
  );
}
//#endregion Selectors
